import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useProjectsStore } from '../store/projects';
import { usePostsStore } from '../store/posts';
import { useFlightsStore } from '../store/flights';
import {
  useMarketingStore,
  EditorialWeek,
  ContentPillar,
  UpdateEditorialWeekInput,
  FunnelStage,
  WeekStatus,
  ContentFormat,
  CTAType,
  CTA,
} from '../store/marketing';
import { PageHeader } from '../components/PageHeader';

// CalendarIcon - matches header navigation
function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

// ============ Constants & Styles ============

// Status styles for consistent card styling
const STATUS_STYLES: Record<string, { cardBg: string; accent: string; dot: string }> = {
  planned: { cardBg: 'bg-slate-50', accent: 'border-l-slate-400', dot: 'bg-slate-400' },
  in_progress: { cardBg: 'bg-amber-50', accent: 'border-l-amber-500', dot: 'bg-amber-500' },
  ready: { cardBg: 'bg-blue-50', accent: 'border-l-blue-500', dot: 'bg-blue-500' },
  published: { cardBg: 'bg-green-50', accent: 'border-l-green-500', dot: 'bg-green-500' },
  skipped: { cardBg: 'bg-zinc-50', accent: 'border-l-zinc-400', dot: 'bg-zinc-400' },
};

// Funnel styles
const FUNNEL_STYLES: Record<string, { color: string; bg: string }> = {
  awareness: { color: 'text-blue-700', bg: 'bg-blue-100' },
  consideration: { color: 'text-purple-700', bg: 'bg-purple-100' },
  activation: { color: 'text-green-700', bg: 'bg-green-100' },
};

// Format icons
const FORMAT_ICONS: Record<string, string> = {
  carousel: '📸',
  reel: '🎬',
  story: '📱',
  single_graphic: '🖼️',
  text: '📝',
};

// ============ Helpers ============

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function getWeekDateRange(year: number, week: number): { start: Date; end: Date } {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

function formatDate(date: Date): string {
  return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}`;
}

// ============ Tooltip Component ============
function Tooltip({ children, text }: { children: React.ReactNode; text: string }) {
  return (
    <div className="relative group inline-block">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 w-64 text-center whitespace-normal">
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    </div>
  );
}

// ============ Multi-Select Checkbox Component ============
function MultiSelectCheckbox({
  label,
  options,
  selectedValues,
  onChange,
  tooltip,
  t,
}: {
  label: string;
  options: { value: string; label: string; color?: string; icon?: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  tooltip?: string;
  t: (key: string) => string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectedLabels = options
    .filter(opt => selectedValues.includes(opt.value))
    .map(opt => opt.icon ? `${opt.icon} ${opt.label}` : opt.label);

  return (
    <div className="relative" ref={dropdownRef}>
      {tooltip ? (
        <Tooltip text={tooltip}>
          <label className="block text-sm font-medium text-gray-700 mb-1 cursor-help border-b border-dashed border-gray-400">
            {label} ℹ️
          </label>
        </Tooltip>
      ) : (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between"
      >
        <span className={`truncate ${selectedValues.length === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
          {selectedValues.length === 0
            ? t('marketing:calendar.selectOption')
            : selectedLabels.length <= 2
              ? selectedLabels.join(', ')
              : `${selectedLabels.length} ${t('marketing:calendar.selected')}`}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {opt.color && (
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
              )}
              <span className="text-sm text-gray-700">
                {opt.icon && <span className="mr-1">{opt.icon}</span>}
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Selected tags */}
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {options.filter(opt => selectedValues.includes(opt.value)).map(opt => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
            >
              {opt.color && (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: opt.color }} />
              )}
              {opt.icon && <span>{opt.icon}</span>}
              {opt.label}
              <button
                type="button"
                onClick={() => toggleValue(opt.value)}
                className="text-blue-500 hover:text-blue-700"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ Week Edit Modal ============
function WeekModal({
  week,
  year,
  weekNumber,
  pillars,
  flights,
  onClose,
  onSave,
  onCreatePost,
  isLoading,
  t,
}: {
  week?: EditorialWeek | null;
  year: number;
  weekNumber: number;
  pillars: ContentPillar[];
  flights: { id: string; name: string; color: string | null }[];
  onClose: () => void;
  onSave: (data: UpdateEditorialWeekInput) => Promise<void>;
  onCreatePost: () => void;
  isLoading: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [mainTopic, setMainTopic] = useState(week?.main_topic || '');
  const [mainTopicSubtitle, setMainTopicSubtitle] = useState(week?.main_topic_subtitle || '');
  const [altTopic, setAltTopic] = useState(week?.alt_topic || '');

  // Multi-select states
  const [selectedPillars, setSelectedPillars] = useState<string[]>(
    week?.pillar_ids?.length ? week.pillar_ids : (week?.pillar_id ? [week.pillar_id] : [])
  );
  const [selectedFlights, setSelectedFlights] = useState<string[]>(
    week?.flight_ids?.length ? week.flight_ids : (week?.flight_id ? [week.flight_id] : [])
  );
  const [selectedFormats, setSelectedFormats] = useState<ContentFormat[]>(
    (week?.formats?.length ? week.formats : (week?.format ? [week.format] : ['carousel'])) as ContentFormat[]
  );
  const [selectedFunnelStages, setSelectedFunnelStages] = useState<FunnelStage[]>(
    (week?.funnel_stages?.length ? week.funnel_stages : (week?.funnel_stage ? [week.funnel_stage] : ['awareness'])) as FunnelStage[]
  );

  // CTAs state (array of {type, text})
  const [ctas, setCtas] = useState<CTA[]>(
    week?.ctas?.length ? week.ctas : (week?.cta_type ? [{ type: week.cta_type, text: week.cta_text || '' }] : [{ type: 'save' as CTAType, text: '' }])
  );

  const [status, setStatus] = useState<WeekStatus>(week?.status || 'planned');
  const [notes, setNotes] = useState(week?.notes || '');
  const [planningComplete, setPlanningComplete] = useState(week?.planning_complete || false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  const { start, end } = getWeekDateRange(year, weekNumber);

  // Auto-resize notes textarea to fit content
  useEffect(() => {
    const textarea = notesRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(80, textarea.scrollHeight)}px`;
    }
  }, [notes]);

  // CTA handlers
  const addCta = () => {
    setCtas([...ctas, { type: 'save' as CTAType, text: '' }]);
  };

  const removeCta = (index: number) => {
    if (ctas.length > 1) {
      setCtas(ctas.filter((_, i) => i !== index));
    }
  };

  const updateCta = (index: number, field: 'type' | 'text', value: string) => {
    const newCtas = [...ctas];
    if (field === 'type') {
      newCtas[index] = { ...newCtas[index], type: value as CTAType };
    } else {
      newCtas[index] = { ...newCtas[index], text: value };
    }
    setCtas(newCtas);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      main_topic: mainTopic,
      main_topic_subtitle: mainTopicSubtitle || undefined,
      alt_topic: altTopic || undefined,
      pillar_ids: selectedPillars,
      flight_ids: selectedFlights,
      formats: selectedFormats,
      funnel_stages: selectedFunnelStages,
      ctas,
      status,
      notes: notes || undefined,
      planning_complete: planningComplete,
    });
  };

  // Prepare options for multi-select
  const pillarOptions = pillars.map(p => ({ value: p.id, label: p.name, color: p.color }));
  const flightOptions = flights.map(f => ({ value: f.id, label: f.name, color: f.color || undefined }));
  const formatOptions = Object.entries(FORMAT_ICONS).map(([value, icon]) => ({
    value,
    label: t(`marketing:formats.${value}`),
    icon
  }));
  const funnelOptions = ['awareness', 'consideration', 'activation'].map(key => ({
    value: key,
    label: t(`marketing:funnel.${key}`)
  }));

  // CTA options
  const ctaOptions = ['save', 'link_bio', 'dm_keyword', 'share'].map(key => ({
    value: key,
    label: t(`marketing:cta.types.${key}`),
    description: t(`marketing:cta.types.${key}Desc`),
  }));

  // Status options
  const statusOptions = ['planned', 'in_progress', 'ready', 'published', 'skipped'].map(key => {
    const statusKey = key === 'in_progress' ? 'inProgress' : key;
    return { value: key, label: t(`marketing:status.${statusKey}`) };
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl transform transition-all max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b z-10">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {week ? t('marketing:calendar.editWeek') : t('marketing:calendar.newWeekPlan')}
              </h3>
              <p className="text-sm text-gray-500">
                {t('marketing:calendar.week')} {weekNumber} / {year} ({formatDate(start)} - {formatDate(end)})
              </p>
            </div>
            <div className="flex items-center gap-2">
              {week && (
                <button
                  type="button"
                  onClick={onCreatePost}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('marketing:calendar.createPost')}
                </button>
              )}
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-6">
            {/* Main Topic */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketing:calendar.mainTopicRequired')}</label>
                <input
                  type="text"
                  value={mainTopic}
                  onChange={(e) => setMainTopic(e.target.value)}
                  placeholder={t('marketing:calendar.mainTopicPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketing:calendar.subtitle')}</label>
                <input
                  type="text"
                  value={mainTopicSubtitle}
                  onChange={(e) => setMainTopicSubtitle(e.target.value)}
                  placeholder={t('marketing:calendar.subtitlePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketing:calendar.altTopic')}</label>
                <input
                  type="text"
                  value={altTopic}
                  onChange={(e) => setAltTopic(e.target.value)}
                  placeholder={t('marketing:calendar.altTopicPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Pillar, Flight, Format, Funnel - Multi-Select */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MultiSelectCheckbox
                label={t('marketing:calendar.pillar')}
                options={pillarOptions}
                selectedValues={selectedPillars}
                onChange={setSelectedPillars}
                t={t}
              />

              <MultiSelectCheckbox
                label={t('marketing:calendar.flight')}
                options={flightOptions}
                selectedValues={selectedFlights}
                onChange={setSelectedFlights}
                t={t}
              />

              <MultiSelectCheckbox
                label={t('marketing:calendar.format')}
                options={formatOptions}
                selectedValues={selectedFormats}
                onChange={(values) => setSelectedFormats(values as ContentFormat[])}
                t={t}
              />

              <MultiSelectCheckbox
                label={t('marketing:calendar.funnel')}
                options={funnelOptions}
                selectedValues={selectedFunnelStages}
                onChange={(values) => setSelectedFunnelStages(values as FunnelStage[])}
                tooltip={t('marketing:calendar.funnelTooltip')}
                t={t}
              />
            </div>

            {/* CTAs - Dynamic list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">{t('marketing:calendar.ctas')}</label>
                <button
                  type="button"
                  onClick={addCta}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('marketing:calendar.addCta')}
                </button>
              </div>
              <div className="space-y-3">
                {ctas.map((cta, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Tooltip text={ctaOptions.find(c => c.value === cta.type)?.description || ''}>
                          <label className="block text-xs text-gray-500 mb-1 cursor-help">{t('marketing:calendar.ctaType')} ℹ️</label>
                        </Tooltip>
                        <select
                          value={cta.type}
                          onChange={(e) => updateCta(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                        >
                          {ctaOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">{t('marketing:calendar.ctaText')}</label>
                        <input
                          type="text"
                          value={cta.text || ''}
                          onChange={(e) => updateCta(index, 'text', e.target.value)}
                          placeholder={t('marketing:calendar.ctaTextPlaceholder')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                    {ctas.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCta(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded mt-5"
                        title={t('marketing:calendar.removeCta')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketing:calendar.status')}</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as WeekStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Planning Complete */}
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
              <input
                type="checkbox"
                id="planningComplete"
                checked={planningComplete}
                onChange={(e) => setPlanningComplete(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="planningComplete" className="flex-1">
                <span className="font-medium text-green-800">{t('marketing:calendar.planningCompleteLabel')}</span>
                <p className="text-sm text-green-600">{t('marketing:calendar.planningCompleteDesc')}</p>
              </label>
              {planningComplete && (
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketing:calendar.notes')}</label>
              <textarea
                ref={notesRef}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('marketing:calendar.notesPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
                style={{ minHeight: '80px' }}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                {t('common:cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading || !mainTopic.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? `${t('common:save')}...` : week ? t('common:save') : t('common:create')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============ Main Component ============
export function MarketingCalendarPage() {
  const { t } = useTranslation(['marketing', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const { posts, fetchPosts } = usePostsStore();
  const { flights, fetchFlights } = useFlightsStore();
  const {
    pillars,
    editorialWeeks,
    currentYear,
    selectedQuarter,
    calendarFilters,
    isLoading,
    error,
    fetchPillars,
    fetchEditorialPlan,
    createEditorialWeek,
    updateEditorialWeek,
    deleteEditorialWeek,
    moveEditorialWeek,
    setCurrentYear,
    setSelectedQuarter,
    setCalendarFilters,
    clearCalendarFilters,
    clearError,
  } = useMarketingStore();

  const [showModal, setShowModal] = useState(false);
  const [editingWeek, setEditingWeek] = useState<EditorialWeek | null>(null);
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Drag & Drop state
  const [draggedWeek, setDraggedWeek] = useState<{ id: string; weekNumber: number } | null>(null);
  const [dropTargetWeek, setDropTargetWeek] = useState<number | null>(null);

  // Use persistent filters from store
  const {
    searchTerm,
    pillarId: filterPillar,
    funnelStage: filterFunnel,
    status: filterStatus,
    groupBy,
    weekFrom: filterWeekFrom,
    weekTo: filterWeekTo,
    dateFrom: filterDateFrom,
    dateTo: filterDateTo,
  } = calendarFilters;

  const currentWeekNumber = getISOWeek(new Date());

  // Auth check
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProjects();
  }, [isAuthenticated, navigate, fetchProjects]);

  // Set project from URL or default
  useEffect(() => {
    const projectIdParam = searchParams.get('project_id');
    const quarterParam = searchParams.get('quarter');

    if (projectIdParam && projects.some((p) => p.id === projectIdParam)) {
      setSelectedProjectId(projectIdParam);
    } else if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }

    if (quarterParam) {
      setSelectedQuarter(parseInt(quarterParam));
    }
  }, [searchParams, projects, selectedProjectId, setSelectedProjectId, setSelectedQuarter]);

  // Load data
  useEffect(() => {
    if (selectedProjectId) {
      fetchPillars(selectedProjectId);
      fetchEditorialPlan(selectedProjectId, currentYear, selectedQuarter || undefined);
      fetchPosts(selectedProjectId);
      fetchFlights(selectedProjectId);
    }
  }, [selectedProjectId, currentYear, selectedQuarter, fetchPillars, fetchEditorialPlan, fetchPosts, fetchFlights]);

  // Generate all weeks for the year
  const allWeeks = useMemo(() => {
    const weeks: { weekNumber: number; quarter: number; data?: EditorialWeek }[] = [];
    for (let w = 1; w <= 52; w++) {
      const quarter = Math.ceil(w / 13);
      const existingWeek = editorialWeeks.find((ew) => ew.week_number === w);
      weeks.push({ weekNumber: w, quarter, data: existingWeek });
    }
    return weeks;
  }, [editorialWeeks]);

  // Apply all filters
  const filteredWeeks = useMemo(() => {
    let result = allWeeks;

    // Quarter filter
    if (selectedQuarter) {
      result = result.filter((w) => w.quarter === selectedQuarter);
    }

    // Week range filter
    if (filterWeekFrom !== '') {
      result = result.filter((w) => w.weekNumber >= filterWeekFrom);
    }
    if (filterWeekTo !== '') {
      result = result.filter((w) => w.weekNumber <= filterWeekTo);
    }

    // Date range filter
    if (filterDateFrom || filterDateTo) {
      result = result.filter((w) => {
        const { start, end } = getWeekDateRange(currentYear, w.weekNumber);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          // Week must end on or after the from date
          if (end < fromDate) return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          // Week must start on or before the to date
          if (start > toDate) return false;
        }
        return true;
      });
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter((w) => {
        if (!w.data) return false;
        return (
          w.data.main_topic.toLowerCase().includes(search) ||
          w.data.main_topic_subtitle?.toLowerCase().includes(search) ||
          w.data.alt_topic?.toLowerCase().includes(search) ||
          w.data.notes?.toLowerCase().includes(search)
        );
      });
    }

    // Pillar filter
    if (filterPillar) {
      result = result.filter((w) => w.data?.pillar_id === filterPillar);
    }

    // Funnel filter
    if (filterFunnel) {
      result = result.filter((w) => w.data?.funnel_stage === filterFunnel);
    }

    // Status filter
    if (filterStatus) {
      result = result.filter((w) => w.data?.status === filterStatus);
    }

    return result;
  }, [allWeeks, selectedQuarter, searchTerm, filterPillar, filterFunnel, filterStatus, filterWeekFrom, filterWeekTo, filterDateFrom, filterDateTo, currentYear]);

  // Group weeks if needed
  const groupedWeeks = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: '', weeks: filteredWeeks }];
    }

    const groups: Record<string, typeof filteredWeeks> = {};

    filteredWeeks.forEach((week) => {
      let groupKey = t('marketing:calendar.notAssigned');

      if (groupBy === 'pillar' && week.data?.pillar_id) {
        const pillar = pillars.find((p) => p.id === week.data?.pillar_id);
        groupKey = pillar?.name || t('marketing:calendar.unknownPillar');
      } else if (groupBy === 'funnel' && week.data?.funnel_stage) {
        groupKey = t(`marketing:funnel.${week.data.funnel_stage}`);
      } else if (groupBy === 'status' && week.data?.status) {
        const statusKey = week.data.status === 'in_progress' ? 'inProgress' : week.data.status;
        groupKey = t(`marketing:status.${statusKey}`);
      } else if (!week.data) {
        groupKey = t('marketing:calendar.notPlanned');
      }

      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(week);
    });

    return Object.entries(groups).map(([key, weeks]) => ({ key, label: key, weeks }));
  }, [filteredWeeks, groupBy, pillars, t]);

  // Get posts for a week's date range
  const getPostsForWeek = (weekNumber: number) => {
    const { start, end } = getWeekDateRange(currentYear, weekNumber);
    return posts.filter((post) => {
      if (!post.scheduled_for) return false;
      const postDate = new Date(post.scheduled_for);
      return postDate >= start && postDate <= end;
    });
  };

  // Get flights active during a week
  const getFlightsForWeek = (weekNumber: number) => {
    const { start, end } = getWeekDateRange(currentYear, weekNumber);
    return flights.filter((flight) => {
      const flightStart = new Date(flight.start_date);
      const flightEnd = new Date(flight.end_date);
      return flightStart <= end && flightEnd >= start;
    });
  };

  const handleCreateWeek = async (data: UpdateEditorialWeekInput) => {
    if (!selectedProjectId) return;
    await createEditorialWeek({
      project_id: selectedProjectId,
      year: currentYear,
      week_number: selectedWeekNumber,
      main_topic: data.main_topic || '',
      main_topic_subtitle: data.main_topic_subtitle || undefined,
      alt_topic: data.alt_topic || undefined,
      pillar_id: data.pillar_id || undefined,
      flight_id: data.flight_id || undefined,
      format: data.format,
      cta_type: data.cta_type,
      cta_text: data.cta_text || undefined,
      funnel_stage: data.funnel_stage,
      status: data.status,
      notes: data.notes || undefined,
    });
    setShowModal(false);
  };

  const handleUpdateWeek = async (data: UpdateEditorialWeekInput) => {
    if (!editingWeek) return;
    await updateEditorialWeek(editingWeek.id, data);
    setEditingWeek(null);
  };

  const handleDeleteWeek = async (week: EditorialWeek) => {
    if (confirm(t('marketing:calendar.deleteWeekConfirm', { weekNumber: week.week_number }))) {
      await deleteEditorialWeek(week.id);
    }
  };

  const handleTogglePlanningComplete = async (week: EditorialWeek, e: React.MouseEvent) => {
    e.stopPropagation();
    await updateEditorialWeek(week.id, { planning_complete: !week.planning_complete });
  };

  const getPillar = (pillarId: string | null): ContentPillar | undefined => {
    return pillars.find((p) => p.id === pillarId);
  };

  // Get status info
  const getStatusInfo = (status: string) => {
    const statusKey = status === 'in_progress' ? 'inProgress' : status;
    return {
      label: t(`marketing:status.${statusKey}`),
      color: status === 'planned' ? 'text-slate-700' :
             status === 'in_progress' ? 'text-amber-700' :
             status === 'ready' ? 'text-blue-700' :
             status === 'published' ? 'text-green-700' : 'text-zinc-500',
      bg: status === 'planned' ? 'bg-slate-100' :
          status === 'in_progress' ? 'bg-amber-100' :
          status === 'ready' ? 'bg-blue-100' :
          status === 'published' ? 'bg-green-100' : 'bg-zinc-100',
      ...STATUS_STYLES[status],
    };
  };

  const hasActiveFilters = searchTerm || filterPillar || filterFunnel || filterStatus || groupBy !== 'none' || filterWeekFrom !== '' || filterWeekTo !== '' || filterDateFrom || filterDateTo;

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent, week: EditorialWeek) => {
    setDraggedWeek({ id: week.id, weekNumber: week.week_number });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', week.id);
    // Add drag image styling
    const target = e.target as HTMLElement;
    target.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    setDraggedWeek(null);
    setDropTargetWeek(null);
  };

  const handleDragOver = (e: React.DragEvent, weekNumber: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedWeek && draggedWeek.weekNumber !== weekNumber) {
      setDropTargetWeek(weekNumber);
    }
  };

  const handleDragLeave = () => {
    setDropTargetWeek(null);
  };

  const handleDrop = async (e: React.DragEvent, targetWeekNumber: number) => {
    e.preventDefault();
    setDropTargetWeek(null);

    if (!draggedWeek || !selectedProjectId) return;
    if (draggedWeek.weekNumber === targetWeekNumber) return;

    try {
      await moveEditorialWeek(draggedWeek.id, targetWeekNumber, selectedProjectId, currentYear);
    } catch (error) {
      console.error('Move failed:', error);
    }

    setDraggedWeek(null);
  };

  // Status options for filter
  const statusFilterOptions = ['planned', 'in_progress', 'ready', 'published', 'skipped'].map(key => {
    const statusKey = key === 'in_progress' ? 'inProgress' : key;
    return { value: key, label: t(`marketing:status.${statusKey}`) };
  });

  // Funnel options for filter
  const funnelFilterOptions = ['awareness', 'consideration', 'activation'].map(key => ({
    value: key,
    label: t(`marketing:funnel.${key}`),
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        icon={<CalendarIcon className="w-7 h-7" />}
        title={t('marketing:calendar.title')}
        subtitle={t('marketing:calendar.subtitle')}
        breadcrumbs={[{ label: t('marketing:breadcrumbs.marketing'), href: '/marketing' }]}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {/* Project selector */}
            <select
              value={selectedProjectId || ''}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
                navigate(`/marketing/calendar?project_id=${e.target.value}`);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>

            {/* Year selector with stepper */}
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setCurrentYear(Math.max(2020, currentYear - 1))}
                className="px-2 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                title={t('marketing:dashboard.previousYear')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <input
                type="number"
                min={2020}
                max={2099}
                value={currentYear}
                onChange={(e) => {
                  const year = parseInt(e.target.value);
                  if (year >= 2020 && year <= 2099) {
                    setCurrentYear(year);
                  }
                }}
                className="w-16 px-1 py-2 text-center text-sm font-medium border-0 focus:ring-0 focus:outline-none"
              />
              <button
                onClick={() => setCurrentYear(Math.min(2099, currentYear + 1))}
                className="px-2 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
                title={t('marketing:dashboard.nextYear')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Quarter filter */}
            <select
              value={selectedQuarter || ''}
              onChange={(e) => setSelectedQuarter(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
            >
              <option value="">{t('marketing:calendar.allQuarters')}</option>
              <option value="1">{t('marketing:calendar.q1')}</option>
              <option value="2">{t('marketing:calendar.q2')}</option>
              <option value="3">{t('marketing:calendar.q3')}</option>
              <option value="4">{t('marketing:calendar.q4')}</option>
            </select>

            {/* View toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        }
      />

      {/* Filters Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        {/* Row 1: Search and Content Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setCalendarFilters({ searchTerm: e.target.value })}
              placeholder={t('marketing:calendar.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Pillar Filter */}
          <select
            value={filterPillar}
            onChange={(e) => setCalendarFilters({ pillarId: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="">{t('marketing:calendar.allPillars')}</option>
            {pillars.map((pillar) => (
              <option key={pillar.id} value={pillar.id}>{pillar.name}</option>
            ))}
          </select>

          {/* Funnel Filter */}
          <select
            value={filterFunnel}
            onChange={(e) => setCalendarFilters({ funnelStage: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="">{t('marketing:calendar.allFunnelStages')}</option>
            {funnelFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setCalendarFilters({ status: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="">{t('marketing:calendar.allStatus')}</option>
            {statusFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Group By */}
          <select
            value={groupBy}
            onChange={(e) => setCalendarFilters({ groupBy: e.target.value as typeof groupBy })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            <option value="none">{t('marketing:calendar.noGrouping')}</option>
            <option value="pillar">{t('marketing:calendar.byPillar')}</option>
            <option value="funnel">{t('marketing:calendar.byFunnel')}</option>
            <option value="status">{t('marketing:calendar.byStatus')}</option>
          </select>
        </div>

        {/* Row 2: Week and Date Range Filters */}
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
          {/* Week Range Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t('marketing:calendar.weekRange')}</span>
            <input
              type="number"
              min={1}
              max={52}
              value={filterWeekFrom}
              onChange={(e) => setCalendarFilters({ weekFrom: e.target.value ? parseInt(e.target.value) : '' })}
              placeholder={t('marketing:calendar.from')}
              className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-center"
            />
            <span className="text-gray-400">–</span>
            <input
              type="number"
              min={1}
              max={52}
              value={filterWeekTo}
              onChange={(e) => setCalendarFilters({ weekTo: e.target.value ? parseInt(e.target.value) : '' })}
              placeholder={t('marketing:calendar.to')}
              className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-center"
            />
          </div>

          <span className="text-gray-300">|</span>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t('marketing:calendar.dateRange')}</span>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setCalendarFilters({ dateFrom: e.target.value })}
              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <span className="text-gray-400">–</span>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setCalendarFilters({ dateTo: e.target.value })}
              className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Quick date presets */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                const now = new Date();
                setCalendarFilters({
                  weekFrom: getISOWeek(now),
                  weekTo: Math.min(getISOWeek(now) + 3, 52),
                  dateFrom: '',
                  dateTo: '',
                });
              }}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
            >
              {t('marketing:calendar.next4Weeks')}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const currentMonth = now.getMonth();
                const yearNow = now.getFullYear();
                const firstDay = new Date(yearNow, currentMonth, 1);
                const lastDay = new Date(yearNow, currentMonth + 1, 0);
                setCalendarFilters({
                  dateFrom: firstDay.toISOString().split('T')[0],
                  dateTo: lastDay.toISOString().split('T')[0],
                  weekFrom: '',
                  weekTo: '',
                });
              }}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
            >
              {t('marketing:calendar.thisMonth')}
            </button>
            <button
              onClick={() => {
                const now = new Date();
                const currentMonth = now.getMonth();
                const yearNow = now.getFullYear();
                const firstDay = new Date(yearNow, currentMonth + 1, 1);
                const lastDay = new Date(yearNow, currentMonth + 2, 0);
                setCalendarFilters({
                  dateFrom: firstDay.toISOString().split('T')[0],
                  dateTo: lastDay.toISOString().split('T')[0],
                  weekFrom: '',
                  weekTo: '',
                });
              }}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-600"
            >
              {t('marketing:calendar.nextMonth')}
            </button>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearCalendarFilters}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('marketing:calendar.clearFilters')}
            </button>
          )}

          {/* Results count */}
          <div className="ml-auto text-sm text-gray-500">
            <span className="font-medium text-gray-900">{filteredWeeks.length}</span>
            {' '}{t('marketing:calendar.weeksOf')}{' '}
            <span>{allWeeks.length}</span>
            {' '}{t('marketing:dashboard.weeks')}
            {filteredWeeks.filter(w => w.data).length > 0 && (
              <span className="text-gray-400">
                {' '}({filteredWeeks.filter(w => w.data).length} {t('marketing:calendar.planned')})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1"><p className="text-red-700">{error}</p></div>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 text-sm bg-gray-50 p-3 rounded-lg">
        <span className="text-gray-500 font-medium">{t('marketing:calendar.legend')}</span>
        {['awareness', 'consideration', 'activation'].map((key) => (
          <Tooltip key={key} text={t(`marketing:funnel.${key}Desc`)}>
            <div className="flex items-center gap-1.5 cursor-help">
              <span className={`w-3 h-3 rounded-full ${FUNNEL_STYLES[key].bg}`} />
              <span className="text-gray-600">{t(`marketing:funnel.${key}`)}</span>
            </div>
          </Tooltip>
        ))}
        <span className="mx-2 text-gray-300">|</span>
        <div className="flex items-center gap-1.5">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-gray-600">{t('marketing:calendar.planningComplete')}</span>
        </div>
        <span className="mx-2 text-gray-300">|</span>
        <Tooltip text={t('marketing:calendar.dragAndDropHint')}>
          <div className="flex items-center gap-1.5 cursor-help">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
            </svg>
            <span className="text-gray-600">{t('marketing:calendar.dragAndDrop')}</span>
          </div>
        </Tooltip>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : (
        <>
          {/* Grouped Content */}
          {groupedWeeks.map(({ key, label, weeks }) => (
            <div key={key} className="mb-8">
              {label && groupBy !== 'none' && (
                <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  {groupBy === 'pillar' && (
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: pillars.find((p) => p.name === label)?.color || '#6B7280' }}
                    />
                  )}
                  {label}
                  <span className="text-sm font-normal text-gray-400">
                    ({weeks.filter(w => w.data).length} {t('marketing:calendar.planned')} / {weeks.length} {t('marketing:dashboard.weeks')})
                  </span>
                </h2>
              )}

              {viewMode === 'grid' ? (
                /* Grid View */
                <div className="grid grid-cols-4 sm:grid-cols-7 md:grid-cols-13 gap-2">
                  {weeks.map(({ weekNumber, data }) => {
                    const { start, end } = getWeekDateRange(currentYear, weekNumber);
                    const pillar = data ? getPillar(data.pillar_id) : undefined;
                    const isCurrent = weekNumber === currentWeekNumber && currentYear === new Date().getFullYear();
                    const isPast = weekNumber < currentWeekNumber && currentYear === new Date().getFullYear();
                    const weekPosts = getPostsForWeek(weekNumber);
                    const weekFlights = getFlightsForWeek(weekNumber);

                    const isDropTarget = dropTargetWeek === weekNumber;
                    const isDragging = draggedWeek?.weekNumber === weekNumber;

                    const statusInfo = data ? getStatusInfo(data.status) : null;

                    return (
                      <div
                        key={weekNumber}
                        draggable={!!data}
                        onDragStart={(e) => data && handleDragStart(e, data)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, weekNumber)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, weekNumber)}
                        onClick={() => {
                          if (data) {
                            setEditingWeek(data);
                          } else {
                            setSelectedWeekNumber(weekNumber);
                            setShowModal(true);
                          }
                        }}
                        className={`relative p-2 rounded-lg border cursor-pointer transition-all hover:shadow-md group ${
                          isCurrent ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
                        } ${isPast && !data ? 'bg-gray-50 opacity-50' : 'bg-white'} ${data ? 'border-l-4' : ''} ${
                          isDropTarget ? 'ring-2 ring-green-500 bg-green-50' : ''
                        } ${isDragging ? 'opacity-50' : ''} ${data ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        style={{ borderLeftColor: pillar?.color || 'transparent' }}
                      >
                        {/* Planning Complete Indicator */}
                        {data?.planning_complete && (
                          <div className="absolute -top-1 -right-1">
                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                            </svg>
                          </div>
                        )}

                        {/* Week Number */}
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs font-bold ${isCurrent ? 'text-blue-600' : 'text-gray-500'}`}>
                            {t('marketing:calendar.week')} {weekNumber}
                          </span>
                          {data && (
                            <span className={`w-1.5 h-1.5 rounded-full ${FUNNEL_STYLES[data.funnel_stage]?.bg}`} />
                          )}
                        </div>

                        {/* Date range */}
                        <div className="text-[10px] text-gray-400 mb-1">
                          {formatDate(start)} - {formatDate(end)}
                        </div>

                        {data ? (
                          <>
                            {/* Topic */}
                            <div className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">
                              {data.main_topic}
                            </div>

                            {/* Thumbnails for posts */}
                            {weekPosts.length > 0 && (
                              <div className="flex -space-x-1 mb-1">
                                {weekPosts.slice(0, 3).map((post) => (
                                  <Link
                                    key={post.id}
                                    to={`/projects/${selectedProjectId}?post=${post.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-5 h-5 rounded border border-white bg-gray-200 flex items-center justify-center text-[8px] hover:ring-2 hover:ring-blue-400"
                                    title={post.text?.slice(0, 50) || 'Post'}
                                  >
                                    📝
                                  </Link>
                                ))}
                                {weekPosts.length > 3 && (
                                  <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center text-[8px] text-gray-500">
                                    +{weekPosts.length - 3}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Flight badges */}
                            {weekFlights.length > 0 && (
                              <div className="flex flex-wrap gap-0.5 mb-1">
                                {weekFlights.slice(0, 2).map((flight) => (
                                  <Link
                                    key={flight.id}
                                    to={`/flights/${flight.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[8px] px-1 rounded truncate max-w-full hover:opacity-80"
                                    style={{ backgroundColor: flight.color || '#E5E7EB', color: '#374151' }}
                                    title={flight.name}
                                  >
                                    {flight.name.slice(0, 8)}
                                  </Link>
                                ))}
                              </div>
                            )}

                            {/* Format & Status */}
                            <div className="flex items-center gap-1">
                              <span className="text-xs">{FORMAT_ICONS[data.format]}</span>
                              <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${statusInfo?.bg} ${statusInfo?.color}`}>
                                <span className={`w-1 h-1 rounded-full ${statusInfo?.dot}`} />
                                {statusInfo?.label}
                              </span>
                            </div>

                          </>
                        ) : (
                          <div className="flex items-center justify-center h-12">
                            <span className="text-gray-300 group-hover:text-blue-500 transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* List View */
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.week')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.date')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.topic')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.pillar')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.format')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.funnel')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.posts')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.status')}</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.complete')}</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('marketing:calendar.tableHeaders.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {weeks.map(({ weekNumber, data }) => {
                        const { start, end } = getWeekDateRange(currentYear, weekNumber);
                        const pillar = data ? getPillar(data.pillar_id) : undefined;
                        const isCurrent = weekNumber === currentWeekNumber && currentYear === new Date().getFullYear();
                        const weekPosts = getPostsForWeek(weekNumber);
                        const weekFlights = getFlightsForWeek(weekNumber);
                        const isDropTarget = dropTargetWeek === weekNumber;
                        const isDragging = draggedWeek?.weekNumber === weekNumber;
                        const statusInfo = data ? getStatusInfo(data.status) : null;

                        return (
                          <tr
                            key={weekNumber}
                            draggable={!!data}
                            onDragStart={(e) => data && handleDragStart(e, data)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, weekNumber)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, weekNumber)}
                            className={`hover:bg-gray-50 ${isCurrent ? 'bg-blue-50' : ''} ${
                              isDropTarget ? 'bg-green-50 ring-2 ring-green-500' : ''
                            } ${isDragging ? 'opacity-50' : ''} ${data ? 'cursor-grab active:cursor-grabbing' : ''}`}
                          >
                            <td
                              className="px-4 py-3 border-l-4"
                              style={{ borderLeftColor: pillar?.color || 'transparent' }}
                            >
                              <span className={`font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-900'}`}>{weekNumber}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">{formatDate(start)} - {formatDate(end)}</td>
                            <td className="px-4 py-3">
                              {data ? (
                                <div>
                                  <div className="font-medium text-gray-900 text-sm">{data.main_topic}</div>
                                  {data.main_topic_subtitle && <div className="text-xs text-gray-500">{data.main_topic_subtitle}</div>}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: pillar?.color || '#d1d5db' }}
                                />
                                <span className="text-sm text-gray-700">
                                  {pillar?.name || <span className="text-gray-400">-</span>}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {data && (
                                <span className="text-sm">
                                  {FORMAT_ICONS[data.format]}{' '}
                                  {t(`marketing:formats.${data.format}`)}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {data && (
                                <Tooltip text={t(`marketing:funnel.${data.funnel_stage}Desc`)}>
                                  <span className={`px-2 py-0.5 text-xs rounded cursor-help ${FUNNEL_STYLES[data.funnel_stage]?.bg} ${FUNNEL_STYLES[data.funnel_stage]?.color}`}>
                                    {t(`marketing:funnel.${data.funnel_stage}`)}
                                  </span>
                                </Tooltip>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {weekPosts.length > 0 && (
                                  <Link
                                    to={`/projects/${selectedProjectId}`}
                                    className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                  >
                                    {weekPosts.length} {t('marketing:calendar.posts')}
                                  </Link>
                                )}
                                {weekFlights.length > 0 && (
                                  <Link
                                    to={`/flights`}
                                    className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                                  >
                                    {weekFlights.length} {t('marketing:calendar.flights')}
                                  </Link>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {data && statusInfo && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} />
                                  {statusInfo.label}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {data && (
                                <button
                                  onClick={(e) => handleTogglePlanningComplete(data, e)}
                                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    data.planning_complete ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                  }`}
                                  title={data.planning_complete ? t('marketing:calendar.planningComplete') : t('marketing:calendar.markAsComplete')}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {data ? (
                                  <>
                                    <button
                                      onClick={() => setEditingWeek(data)}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      title={t('common:edit')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteWeek(data)}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                      title={t('common:delete')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => { setSelectedWeekNumber(weekNumber); setShowModal(true); }}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                    title={t('marketing:calendar.planWeek')}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Create Modal */}
      {showModal && (
        <WeekModal
          year={currentYear}
          weekNumber={selectedWeekNumber}
          pillars={pillars}
          flights={flights.map((f) => ({ id: f.id, name: f.name, color: f.color }))}
          onClose={() => setShowModal(false)}
          onSave={handleCreateWeek}
          onCreatePost={() => {
            // Navigate to posts page with create dialog
            navigate(`/posts?project=${selectedProjectId}&create=true&week=${selectedWeekNumber}&year=${currentYear}`);
          }}
          isLoading={isLoading}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {editingWeek && (
        <WeekModal
          week={editingWeek}
          year={editingWeek.year}
          weekNumber={editingWeek.week_number}
          pillars={pillars}
          flights={flights.map((f) => ({ id: f.id, name: f.name, color: f.color }))}
          onClose={() => setEditingWeek(null)}
          onSave={handleUpdateWeek}
          onCreatePost={() => {
            // Navigate to posts page with create dialog and editorial week context
            navigate(`/posts?project=${selectedProjectId}&create=true&week=${editingWeek.week_number}&year=${editingWeek.year}&editorial_week=${editingWeek.id}`);
          }}
          isLoading={isLoading}
          t={t}
        />
      )}
    </div>
  );
}
